import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

import Constants from '../../constants/constants';
import StorageKeys from '../../constants/storage';
import { environment } from '../../../../environments/environment';

import { RestApiService, StorageService } from '../../services';
import { AppInitializerService } from '../../services/app-initializer.service';
import { SocketService } from '../../services/socket.service';

/**
 * Broadcast Message Component
 * Displays important announcements or alerts to users across the application
 * Typically used for system notifications, maintenance messages, or promotional content
 */
@Component({
  selector: 'app-broadcast-message',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './broadcast-message.component.html',
  styleUrl: './broadcast-message.component.scss'
})
export class BroadcastMessageComponent implements OnInit {
  contactForm: FormGroup;
  isSubmitting: boolean = false;
  apiError: string = '';
  customerId: string | null = null;

  broadcastMessage: any = null; // This will be populated from app configuration loaded by AppInitializerService

  showPopup: boolean = false;

  constructor(
    private fb: FormBuilder,
    private restAPI: RestApiService,
    private storageService: StorageService,
    private appInitializerService: AppInitializerService,
    private socketService: SocketService
  ) {
    const config = this.appInitializerService.getConfiguration();
    this.broadcastMessage = config?.uiFeatures?.broadcastMessage || null;
    this.customerId = this.storageService.getItemFromSessionStorage(StorageKeys.CUSTOMER_ID);

    if (!this.customerId) {
      // If customerId is not available then create customer and get customerId before showing broadcast message
      this.createCustomer();
    }

    // Initialize contact form with validation
    this.contactForm = this.fb.group({
      firstName: ['', [Validators.pattern(Constants.REGEX.FIRST_NAME)]],
      email: ['', [Validators.pattern(Constants.REGEX.EMAIL)]],
      phoneNumber: ['', [Validators.pattern(Constants.REGEX.PHONE)]]
    }, { validators: this.atLeastOneContactValidator });
  }

  // Create a new customer
  createCustomer(): void {
    const payload = {
      acqPartyId: environment.PRODUCT,
      module: environment.MODULE,
      subModule: environment.SUB_MODULE,
      version: environment.IDENTIFIER,
      ...this.storageService.getUtmParams()
    };
    // Call the REST API to create a new customer
    this.restAPI.post('/v1/customers', payload).subscribe({
      next: (response) => {
        this.customerId = response.customer_id;

        this.storageService.setItemIntoSessionStorage(StorageKeys.CUSTOMER_ID, this.customerId);

        this.storageService.removeItemFromSessionStorage(StorageKeys.SITE_ID);
      }, error: (error) => {
        console.error('Error creating customer:', error);
      }
    });
  }

  ngOnInit() {
    // Show popup on component initialization if broadcast message is available and visible
    if (this.broadcastMessage && this.broadcastMessage.isVisible) {
      this.showPopup = true;
    }

    // Clear API error when user edits any field
    this.contactForm.valueChanges.subscribe(() => {
      if (this.apiError) {
        this.apiError = '';
      }
    });

    // Handle phone number formatting
    this.contactForm.get('phoneNumber')?.valueChanges.subscribe(value => {
      const phoneNumber = value?.replace(/[^\d+]/g, '').slice(0, 13);
      if (phoneNumber !== value) {
        this.contactForm.patchValue({ phoneNumber }, { emitEvent: false });
      }
    });

    // Trigger validation when email or phoneNumber changes
    this.contactForm.get('email')?.valueChanges.subscribe(() => {
      this.contactForm.updateValueAndValidity({ emitEvent: false });
    });

    this.contactForm.get('phoneNumber')?.valueChanges.subscribe(() => {
      this.contactForm.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Close the broadcast message popup
   */
  closePopup() {
    this.showPopup = false;
  }

  // Get background color class based on message type
  getTypeClass(): string {
    switch (this.broadcastMessage?.type) {
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  }

  // Get icon color class based on message type
  getIconClass(): string {
    switch (this.broadcastMessage?.type) {
      case 'warning':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      case 'info':
      default:
        return 'text-blue-600';
    }
  }

  // Custom validator to ensure at least one contact method is provided
  private atLeastOneContactValidator(control: AbstractControl): ValidationErrors | null {
    const email = control.get('email')?.value;
    const phoneNumber = control.get('phoneNumber')?.value;

    if (!email && !phoneNumber) {
      return { atLeastOneContact: true };
    }

    return null;
  }

  // Check if at least one contact method error should be shown
  showAtLeastOneContactError(): boolean {
    const emailControl = this.contactForm.get('email');
    const phoneControl = this.contactForm.get('phoneNumber');
    
    return this.contactForm.errors?.['atLeastOneContact'] && 
           (emailControl?.touched || phoneControl?.touched);
  }

  // Get error message for form fields
  getError(fieldName: string): string {
    const control = this.contactForm.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required.`;
      } else if (control.errors['pattern']) {
        switch (fieldName) {
          case 'firstName':
            return 'Please enter a valid first name (2-50 characters, letters, spaces, hyphens, and apostrophes only)';
          case 'email':
            return 'Please enter a valid email address (e.g., user@example.com)';
          case 'phoneNumber':
            return 'Please enter a valid UK phone number (e.g., 07123456789, 02071234567, +447123456789)';
          default:
            return `Please enter a valid ${fieldName}.`;
        }
      }
    }
    return '';
  }

  // Handle Next button click - create contact
  onNext(): void {
    if (this.contactForm.valid) {
      this.isSubmitting = true;
      this.apiError = '';

      const firstName = this.contactForm.get('firstName')?.value;
      const email = this.contactForm.get('email')?.value;
      const phoneNumber = this.contactForm.get('phoneNumber')?.value;

      const contactDetailsValue = {
        contacts: [{
          ...(firstName && {
            firstName: firstName.trim()?.toLowerCase(),
            foreName: firstName.trim()?.toLowerCase()
          }),
          ...(email && {
            emailId: email.trim()?.toLowerCase(),
            email: email.trim()?.toLowerCase()
          }),
          ...(phoneNumber && {
            phoneNumber: phoneNumber.trim()
          }),
          source: 'CUSTOMER-INPUT',
          status: 'ACTIVE',
          initiateClientEmail: 'NO',
          contactCustomer: true
        }]
      };

      this.restAPI.post(`/v1/customers/${this.customerId}/contacts`, contactDetailsValue).subscribe({
        next: (response: any) => {
          const contactId = response.contactInfo[0].contact_id;
          const customerId = response.contactInfo[0].customer_id;

          // Store contact details
          this.storageService.setItemIntoSessionStorage(StorageKeys.CONTACT_ID, contactId);
          this.storageService.setItemIntoSessionStorage(StorageKeys.CONTACT_DETAILS, {
            contactId: contactId,
            customerId: customerId,
            firstName: response.contactInfo[0].forename || response.contactInfo[0].firstName,
            email: response.contactInfo[0].email_id,
            phoneNumber: response.contactInfo[0].phone_number
          });

          // Socket event to update user name across all sessions
          this.socketService.updateUserName();

          this.isSubmitting = false;
          this.closePopup();
        },
        error: (error: any) => {
          this.apiError = error.error?.description || 'An error occurred while saving contact details.';
          this.isSubmitting = false;
        }
      });
    } else {
      this.contactForm.markAllAsTouched();
    }
  }
}
